import { useEffect, useState } from "react";
import { logger } from "@/lib/logger";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PackageCheck } from "lucide-react";
import { stageViews } from "@/lib/stages";

const fmt = (n) => Math.round(Number(n || 0)).toLocaleString("en-IN"); // production units are whole pieces

// ctx = { order, idx } for an OSP stage. Partial receipts allowed; the rest stays pending at the vendor.
export default function ReceiveFromVendorDialog({ ctx, gatePasses = [], onClose, onSubmit }) {
  const [qty, setQty] = useState("");
  const [saving, setSaving] = useState(false);
  const resetKey = ctx ? `${ctx.order.id}:${ctx.idx}` : "";
  useEffect(() => { if (resetKey) setQty(""); }, [resetKey]);
  if (!ctx) return null;
  const { order, idx } = ctx;
  const v = stageViews(order)[idx];
  if (!v) return null;
  const openPasses = gatePasses.filter((g) => g.order_id === order.id && g.stage_index === idx && g.status === "With Vendor");
  const num = Number(qty);
  const valid = qty !== "" && num > 0 && num <= v.atVendor + 1e-6;
  const leftover = valid ? v.atVendor - num : 0;

  const submit = async (e) => {
    e.preventDefault();
    if (!valid) return;
    setSaving(true);
    try { await onSubmit(order, idx, num); onClose(); }
    catch (err) { logger.error("Receive-from-vendor failed (toast shown by caller):", err); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="receive-from-vendor-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><PackageCheck className="h-4 w-4 text-emerald-600" /> Receive from Vendor · {order.order_code}</DialogTitle>
          <DialogDescription>
            Stage {idx + 1} · <b>{v.name}</b>{v.vendorName ? ` — ${v.vendorName}` : ""}{openPasses.length ? ` · ${openPasses.map((g) => g.gp_number).join(" & ")} open` : ""}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="rounded-lg border border-slate-200 p-3 grid grid-cols-2 gap-2">
            <div className="text-center">
              <p className="text-[10px] uppercase tracking-wide text-slate-400">At Vendor (Pending to Receive)</p>
              <p className="font-mono-data text-lg font-semibold text-orange-700" data-testid="receive-pending">{fmt(v.atVendor)}</p>
            </div>
            <div className="text-center">
              <p className="text-[10px] uppercase tracking-wide text-slate-400">Received So Far</p>
              <p className="font-mono-data text-lg font-semibold text-emerald-700">{fmt(v.done)}</p>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Quantity Received Now</Label>
            <div className="flex gap-2">
              <Input type="number" min="0.001" step="any" max={v.atVendor} value={qty} onChange={(e) => setQty(e.target.value)} placeholder={`max ${fmt(v.atVendor)}`} required autoFocus className="font-mono-data" data-testid="receive-qty" />
              <Button type="button" variant="outline" onClick={() => setQty(String(v.atVendor))} className="shrink-0 text-xs" data-testid="receive-all">All ({fmt(v.atVendor)})</Button>
            </div>
            {qty !== "" && num > v.atVendor && <p className="text-[11px] text-red-600">Only {fmt(v.atVendor)} pending at the vendor.</p>}
            {valid && leftover > 1e-6 && (
              <p className="text-[11px] text-slate-500" data-testid="receive-leftover-note">{fmt(leftover)} pcs will remain <b>Pending to Receive</b> — log another receipt when they arrive.</p>
            )}
            {valid && leftover <= 1e-6 && <p className="text-[11px] text-emerald-600">Everything at the vendor comes back — stage fully received.</p>}
          </div>
          <DialogFooter className="justify-between">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={!valid || saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="receive-submit">
              {saving ? "Receiving…" : valid ? `Receive ${fmt(num)} pcs` : "Receive"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
